import {
  compose,
  withProps,
  branch,
  renderNothing,
  withStateHandlers,
} from 'recompose'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import moment from 'moment'
import find from 'lodash/find'
import isMatch from 'lodash/isMatch'
import range from 'lodash/range'

import { SERVER_DATE } from '../../../../../constants/formats'
import { LOAN_STATUS } from '../../../../../constants/loan'
import { cancelEdit } from '../../../../../actions/loan'
import {
  createHomeLoan,
  deleteHomeLoan,
  updateHomeLoan,
} from '../../../../../actions/home'
import { selectHomeUuid } from '../../../../../store/selectors/home'
import { getLoanInterestRate } from '../../../../../store/selectors/home/loans'
import { withTranslatedLoanTerms } from './loanTermOptions'
import { withTranslatedLoanTypes } from './loanTypeOptions'

const monthOptions = moment.months()
const thisYear = moment().year()
const thirtyYearsAgo = thisYear - 30
const yearOptions = range(thisYear, thirtyYearsAgo).map(n => n.toString())

const EditLoanContainer = compose(
  connect(
    state => ({
      monthOptions,
      yearOptions,
      homeUuid: selectHomeUuid(state),
      ...state.loan,
    }),
    dispatch => ({
      cancelEdit: () => dispatch(cancelEdit()),
      createLoan: (homeUuid, data) => dispatch(createHomeLoan(homeUuid, data)),
      deleteLoanFn: data => dispatch(deleteHomeLoan(data)),
      updateLoan: data => dispatch(updateHomeLoan(data)),
    })
  ),
  branch(({ currentLoan }) => !currentLoan, renderNothing),
  injectIntl,
  withTranslatedLoanTerms,
  withTranslatedLoanTypes,
  withProps(({ currentLoan: { date } }) => {
    const loanMoment = moment(date, SERVER_DATE)
    return {
      loanMoment: loanMoment.isValid() ? loanMoment : moment(),
    }
  }),
  withProps(({ currentLoan: { id } }) => ({ adding: !id })),
  withProps(({ adding, createLoan, updateLoan, homeUuid }) => ({
    submitFn: adding ? data => createLoan(homeUuid, data) : updateLoan,
  })),
  withProps(({ currentLoan, deleteLoanFn }) => ({
    deleteLoan: () => deleteLoanFn(currentLoan),
  })),
  withStateHandlers(
    ({ currentLoan, loanMoment, termOptions, typeOptions }) => ({
      amount: currentLoan.amount,
      interestRate: getLoanInterestRate(currentLoan),
      pmiMonthly: currentLoan.pmiMonthly,
      selectedMonth: loanMoment.format('MMMM'),
      selectedYear: loanMoment.format('YYYY'),
      selectedTerm:
        termOptions.find(({ value, ...restOption }) =>
          isMatch(currentLoan, restOption)
        ) || termOptions[0],
      selectedType:
        typeOptions.find(option => option.loanType === currentLoan.loanType) ||
        typeOptions[0],
      status: currentLoan.status || LOAN_STATUS.CURRENT,
    }),
    {
      onChangeAmount: () => (e, amount) => ({ amount }),
      onChangeInterestRate:
        () =>
        ({ target: { value: interestRate } }) => ({
          interestRate,
        }),
      onChangeMonth: () => selectedMonth => ({ selectedMonth }),
      onChangePmiMonthly: () => (e, pmiMonthly) => ({ pmiMonthly }),
      onChangeYear: () => selectedYear => ({ selectedYear }),
      updateStatus: () => status => ({ status }),
      onChangeTerm:
        (state, { termOptions }) =>
        selectedTerm => ({
          selectedTerm: find(termOptions, { value: selectedTerm }),
        }),
      onChangeType:
        (state, { typeOptions }) =>
        selectedType => ({
          selectedType: find(typeOptions, { value: selectedType }),
        }),
    }
  ),
  withProps(({ loanMoment, updateStatus }) => ({
    onChangeStatus: status => () => updateStatus(status),
  })),
  withProps(
    ({
      amount,
      currentLoan,
      interestRate,
      pmiMonthly,
      selectedMonth,
      selectedYear,
      status,
      submitFn,
      selectedTerm: { value: termValue, ...restTerm },
      selectedType: { value: typeValue, ...restType },
    }) => ({
      onSubmit: event => {
        event.preventDefault()
        submitFn({
          amount,
          date: moment(`${selectedMonth} ${selectedYear}`, 'MMMM YYYY').format(
            SERVER_DATE
          ),
          homeId: currentLoan.homeId,
          loanUuid: currentLoan.loanId,
          pmiMonthly,
          rate: interestRate,
          status,
          ...restTerm,
          ...restType,
        })
      },
    })
  )
)

export default EditLoanContainer
